import {
  ChangeEvent,
  DragEventHandler,
  FC,
  MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { getFile, getValidFiles, removeFile, removeInvalidFile } from './utils';

import Button from '../Button';
import ConfirmationModal from '../ConfirmationModal';
import Container from '../Container';
import Icon from '../Icon';
import Input from '../Input';
import Typography from '../Typography';
import FileList from './FileList';

import './FileUploader.scss';

import { SBFile, TestMetadata } from '../types';

export interface FileUploaderProps {
  /** delete file */
  handleFileDelete?: (
    file: SBFile,
    fileList: SBFile[],
    rawFiles?: FileList | null,
  ) => Promise<void> | void;
  /** upload file */
  /* rawFiles will be the values set on the file input. Because you can't adjust a file inputs value for security reasons this may contain stale data. Always check against the file list provided in the first argument. */
  handleFileUpload: (
    fileList: SBFile[],
    rawFiles: FileList,
  ) => Promise<SBFile[] | void>;
  /** button label */
  labelText?: string;
  /** description under button */
  description?: string;
  /** Uploading text */
  uploadingText?: string;
  /** Drag and drop text */
  dragLabel?: string;
  /** handle multiple file uploads */
  multiple?: boolean;
  /* Comma separated file types (ie. .pdf, .doc, etc) */
  accept?: string;
  /* How to display files */
  displayFiles?: 'string' | 'list';
  /* Class name */
  className?: string;
  /* Input ID */
  id?: string;
  /* Existing Files (these will not be attached to the input, only displayed) */
  value?: SBFile[];
  /* Max File Size */
  maxSize?: number;
  /** Props to pass to the button */
  buttonProps?: Record<string, unknown>;
  /** Width of the Input */
  width?: 'large' | 'auto';
  /* Disable */
  isDisabled?: boolean;
  /* Read Only */
  isReadOnly?: boolean;
  /** Settings for confirmation modal when deleting file. If not set the confirmation modal won't be shown. */
  deleteFileConfirmationModal?: {
    title: string;
    text: string;
  };
  /** Hide close button when uploading a file */
  hideClose?: boolean;
  /** Sets test id */
  testMetadata?: {
    div?: TestMetadata;
    button?: TestMetadata;
    input?: TestMetadata;
    label?: TestMetadata;
    list?: TestMetadata;
    delete?: TestMetadata;
    clearInvalid?: TestMetadata;
    dropZone?: TestMetadata;
    download?: TestMetadata;
  };
}

const FileUploader: FC<FileUploaderProps> = ({
  handleFileUpload,
  handleFileDelete,
  labelText = 'Choose File(s)',
  description,
  displayFiles = 'list',
  id = 'file-upload',
  className,
  multiple = false,
  value,
  accept,
  maxSize,
  buttonProps,
  deleteFileConfirmationModal,
  testMetadata,
  width = 'large',
  isDisabled = false,
  uploadingText = 'Drop your file(s) to upload',
  dragLabel = 'Drag & drop or',
  isReadOnly,
  hideClose = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Event Ref tracks the drag enter / drag leave events - as children trigger them we wait for it to hit 0 to remove the dragging logic.
  const eventRef = useRef<number>(0);
  const cancelRef = useRef<SBFile[]>([]);
  // We maintain a combined file list of uploaded and passed in files for display
  const [files, setFiles] = useState<SBFile[] | []>(value || []);
  const [isDragging, setDragging] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<SBFile | null>(null);
  const fileUploaderClassNames = useMemo(
    () =>
      classNames(
        'sb-file-uploader',
        'sb-file-uploader__text',
        {
          'sb-file-uploader__has-files': files.length > 0,
          'sb-file-uploader__dragging': isDragging,
          'sb-file-uploader__size-large': width === 'large',
          'sb-file-uploader__size-auto': width === 'auto',
        },
        className,
      ),
    [className, isDragging, files, width],
  );

  const removeInvalid = useCallback(
    (file) => {
      setFiles(removeInvalidFile(file, files));
    },
    [files, setFiles],
  );

  const deleteFile = useCallback(
    async (file) => {
      setFiles((files) =>
        files.map((prevFile) =>
          prevFile.id === file.id ? { ...file, deleting: true } : prevFile,
        ),
      );

      const newFiles = removeFile(file, files);
      if (file.new) {
        cancelRef.current = [...cancelRef.current, file];
      }

      try {
        handleFileDelete &&
          (await handleFileDelete(file, newFiles, inputRef.current?.files));
        setFiles(newFiles);
      } catch {
        setFiles(files);
      }
    },
    [files, handleFileDelete, setFiles],
  );

  const uploadFiles = useCallback(
    async (filesUploaded: FileList): Promise<void> => {
      const newFiles = Array.from(filesUploaded).map((file) => {
        return getFile(file, multiple ? files : [], accept, maxSize);
      });

      const mergedFiles = multiple ? [...files, ...newFiles] : newFiles;
      setFiles(mergedFiles);

      let response: SBFile[] | void | false = [];
      try {
        response =
          handleFileUpload &&
          getValidFiles(mergedFiles).length > 0 &&
          (await handleFileUpload(getValidFiles(mergedFiles), filesUploaded));
      } catch {
        response = mergedFiles.map((file) => {
          return {
            ...file,
            id: `${new Date().getTime()}-${file.size} `,
            error: true,
          };
        });
      }

      setFiles(
        mergedFiles.reduce((fileList: SBFile[], file: SBFile): SBFile[] => {
          if (
            cancelRef.current.some(
              (cancelledFile) => cancelledFile.name === file.name,
            )
          ) {
            return fileList;
          }
          const uploaded =
            response &&
            response.find((uploaded) => uploaded.name === file.name);

          fileList.push({
            ...(uploaded && { ...uploaded }),
            ...file,
            new: false,
          });
          return fileList;
        }, [] as SBFile[]),
      );
      cancelRef.current = [];
    },
    [handleFileUpload, files, multiple, accept, maxSize, cancelRef],
  );

  const handleDrop: DragEventHandler = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      event?.dataTransfer?.files && uploadFiles(event.dataTransfer.files);
      eventRef.current = 0;
      setDragging(false);
    },
    [uploadFiles],
  );

  const handleDragOver: DragEventHandler = useCallback(
    (event: MouseEvent) => {
      if (isDisabled || isReadOnly) return false;
      event.stopPropagation();
      event.preventDefault();
      eventRef.current = eventRef.current + 1;
      setDragging(true);
    },
    [setDragging, isDisabled, isReadOnly],
  );

  const handleDragLeave: DragEventHandler = useCallback(
    (event: MouseEvent) => {
      if (isDisabled || isReadOnly) return false;
      event.stopPropagation();
      event.preventDefault();
      eventRef.current = eventRef.current - 1;
      if (eventRef.current === 0) {
        setDragging(false);
      }
    },
    [isDisabled, isReadOnly],
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      inputRef?.current?.click();
    },
    [inputRef],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      event?.target?.files?.length &&
        event.target.files.length > 0 &&
        uploadFiles(event.target.files);
      event.target.value = '';
    },
    [uploadFiles],
  );

  const handleDeleteFile = useCallback(
    (file: SBFile) => {
      if (deleteFileConfirmationModal) {
        setFileToDelete(file);
      } else {
        deleteFile(file);
      }
    },
    [deleteFile, deleteFileConfirmationModal],
  );

  return (
    <div className={fileUploaderClassNames} {...testMetadata?.div}>
      <div
        className="sb-file-uploader__container"
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        {...testMetadata?.dropZone}
      >
        {isDragging && (
          <div className="sb-file-uploader__dragging-container">
            <Icon
              faPackage="fas"
              iconName="fa-arrow-up-from-bracket"
              iconType="fa"
              size="small"
              className="sb-file-uploader__dragging-text"
            />
            <Typography
              className="sb-file-uploader__dragging-text"
              variant="T15"
            >
              {uploadingText}
            </Typography>
          </div>
        )}
        {!isReadOnly && (
          <div
            className={classNames('sb-file-uploader__upload', {
              'sb-file-uploader__upload--dragging': isDragging,
            })}
          >
            <div className="sb-file-uploader__button-container">
              <Typography className="sb-file-uploader__text" variant="T15">
                {dragLabel}
              </Typography>
              <Button
                {...buttonProps}
                className="sb-file-uploader__button"
                {...testMetadata?.button}
                styleType="promoted"
                onClick={handleClick}
                disabled={isDisabled}
              >
                {labelText}
              </Button>
              <Input
                {...testMetadata?.input}
                id={id}
                type="file"
                multiple={multiple}
                className="sb-file-uploader__input-hidden"
                inputRef={inputRef}
                onChange={handleChange}
                accept={accept}
              />
              {displayFiles == 'string' && (
                <span className="sb-file-uploader__label">
                  {files.map((file: SBFile) => file.name).join(', ')}
                </span>
              )}
            </div>
            <div>
              {description && (
                <Typography
                  variant="T15"
                  className="sb-file-uploader__description"
                >
                  {description}
                </Typography>
              )}
            </div>
          </div>
        )}
      </div>

      {files.length == 1 &&
        isReadOnly &&
        !multiple &&
        displayFiles === 'string' && (
          <Container className="sb-file-uploader__read-only-single-file">
            <Typography variant="T15">{files[0].name}</Typography>
          </Container>
        )}

      {files.length == 0 && isReadOnly && (
        <Container className="sb-file-uploader__read-only-empty-message">
          <Typography variant="T15">No files have been uploaded.</Typography>
        </Container>
      )}

      {displayFiles === 'list' && files.length > 0 && (
        <FileList
          files={files}
          onDelete={handleDeleteFile}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          removeInvalid={removeInvalid}
          hideClose={hideClose}
          testMetadata={{
            container: testMetadata?.list,
            delete: testMetadata?.delete,
            clearInvalid: testMetadata?.clearInvalid,
            download: testMetadata?.download,
          }}
        />
      )}
      {deleteFileConfirmationModal && fileToDelete && (
        <ConfirmationModal
          title={deleteFileConfirmationModal.title}
          text={deleteFileConfirmationModal.text}
          onClose={() => {
            setFileToDelete(null);
          }}
          onConfirm={() => {
            deleteFile(fileToDelete);
            setFileToDelete(null);
          }}
          messageType="warning"
          bodyOverlay="blackout"
        />
      )}
    </div>
  );
};

export default FileUploader;
